import { mixer } from './audio-mixer.js';
import type { VoicePlacement } from './voice-director.js';

/** The dials of the incantation treatment; every one can be changed live from the scenario viewer (`scenario.castVoice.set`). */
export interface SpellVoiceSettings {
  /** Semitones to drop the whole line (the recording plays slower by the same step). */
  pitch: number;
  /** Dark low-shelf boost around 150 Hz, in dB. */
  body: number;
  /** A quiet second copy, this many semitones lower and a few milliseconds late, which thickens the voice. It fades out with the line so it never trails after it. */
  doubleSemitones: number; doubleLevel: number; doubleDelay: number;
  /** Reverb return level (1 is the dry level), tail length in seconds and the cutoff that keeps the tail dark. */
  reverb: number; tail: number; tailCutoff: number;
  /** The reverb hears only the voice above this frequency (Hz), so the tail is thin and airy instead of a deep boom. */
  reverbLow: number;
  /** Level of the background bed layered under the line (0 is off), and how many seconds before the voice it starts (negative: after). */
  background: number; backgroundLead: number;
  /** Dry level before the master mixer. */
  level: number;
}
export const SPELL_VOICE_DEFAULTS: SpellVoiceSettings = {
  pitch: 2.7, body: 9, doubleSemitones: 1, doubleLevel: .2, doubleDelay: .0018,
  reverb: 1.15, tail: 4, tailCutoff: 9000, reverbLow: 20, background: 1, backgroundLead: -1, level: 0.85,
};

/** Plays a recorded incantation with the treatment that makes it sound like magic: pitched down, heavier in the low end, doubled, compressed and ringing in a dark hall. Presentation only, in the browser. */
export class SpellVoice {
  readonly settings: SpellVoiceSettings = { ...SPELL_VOICE_DEFAULTS };
  private hall: { buffer: AudioBuffer; seconds: number } | undefined;

  set(change: Partial<SpellVoiceSettings>) { Object.assign(this.settings, change); }
  reset() { Object.assign(this.settings, SPELL_VOICE_DEFAULTS); }

  /** Decodes the recording so a play starts at once. Returns false when audio is unavailable. */
  async preload(url: string): Promise<boolean> { return !!await mixer.engine.decode(url); }

  /** Plays the line once with the current settings. Returns how many seconds it lasts, or 0 when nothing played (not decoded, muted, or the page has not been clicked yet). */
  play(url: string, backgroundUrl?: string, placement?: VoicePlacement): number {
    const graph = mixer.engine.graph('voices'), buffer = mixer.engine.buffer(url);
    if (!graph || !buffer || mixer.voiceGain <= 0) return 0;
    const { context, output } = graph;
    const s = this.settings, rate = 2 ** (-s.pitch / 12);
    const out = context.createGain(), pan = context.createStereoPanner();
    out.gain.value = s.level * (placement?.strength ?? 1) * mixer.engine.match(url); pan.pan.value = placement?.pan ?? 0;
    out.connect(pan); pan.connect(output);

    const voice = context.createGain();
    const shelf = context.createBiquadFilter(); shelf.type = 'lowshelf'; shelf.frequency.value = 150; shelf.gain.value = s.body;
    const squeeze = context.createDynamicsCompressor(); squeeze.threshold.value = -26; squeeze.knee.value = 12; squeeze.ratio.value = 4; squeeze.attack.value = .004; squeeze.release.value = .18;
    voice.connect(shelf); shelf.connect(squeeze); squeeze.connect(out);

    const nodes: AudioNode[] = [voice, shelf, squeeze, out, pan];
    const start = (rateOf: number, level: number, delay: number) => {
      const source = context.createBufferSource(), gain = context.createGain(), at = context.currentTime + delay;
      source.buffer = buffer; source.playbackRate.value = rateOf; gain.gain.value = level;
      source.connect(gain); gain.connect(voice); nodes.push(source, gain);
      source.start(at); return { source, gain, end: delay + buffer.duration / rateOf };
    };
    // A positive lead starts the bed first (the voice waits); a negative one starts the voice first.
    const lead = backgroundUrl ? Math.max(0, s.backgroundLead) : 0, voiceAt = lead, bedAt = Math.max(0, -s.backgroundLead);
    const main = start(rate, 1, voiceAt);
    const second = s.doubleLevel > 0 ? start(rate * 2 ** (-s.doubleSemitones / 12), s.doubleLevel, voiceAt + s.doubleDelay) : undefined;
    if (second) {
      // Lower pitch plays longer; the double is faded out and cut where the main line ends, so no deep voice is left echoing after it.
      const stopAt = context.currentTime + main.end, fadeFrom = Math.max(context.currentTime + voiceAt, stopAt - .3);
      second.gain.gain.setValueAtTime(s.doubleLevel, fadeFrom); second.gain.gain.linearRampToValueAtTime(0, stopAt); second.source.stop(stopAt);
    }
    const length = main.end;

    const bed = backgroundUrl ? mixer.engine.buffer(backgroundUrl) : undefined;
    let bedEnd = 0;
    if (bed && s.background > 0) {
      // The bed is not treated: it already carries its own space, and sits beside the voice, not behind its reverb.
      const source = context.createBufferSource(), gain = context.createGain();
      source.buffer = bed; gain.gain.value = s.background; source.connect(gain); gain.connect(out); nodes.push(source, gain);
      source.start(context.currentTime + bedAt); bedEnd = bedAt + bed.duration;
    }
    let end = Math.max(length, bedEnd);
    if (s.reverb > 0) {
      const wet = context.createGain(), dark = context.createBiquadFilter(), thin = context.createBiquadFilter(), room = context.createConvolver();
      thin.type = 'highpass'; thin.frequency.value = s.reverbLow;
      dark.type = 'lowpass'; dark.frequency.value = s.tailCutoff; wet.gain.value = s.reverb;
      room.buffer = this.hallFor(context, s.tail);
      voice.connect(thin); thin.connect(room); room.connect(dark); dark.connect(wet); wet.connect(out);
      nodes.push(wet, dark, thin, room); end += s.tail;
    }
    main.source.onended = () => setTimeout(() => { for (const node of nodes) node.disconnect(); }, (end - main.end) * 1000 + 200);
    return length;
  }

  /** A decaying-noise hall, rebuilt only when the tail length changes. */
  private hallFor(context: AudioContext, seconds: number): AudioBuffer {
    if (this.hall?.seconds === seconds) return this.hall.buffer;
    const length = Math.floor(context.sampleRate * seconds), buffer = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const data = buffer.getChannelData(channel);
      for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2.5;
    }
    this.hall = { buffer, seconds }; return buffer;
  }
}

export const spellVoice = new SpellVoice();
